import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { i18n, SUPPORTED_LOCALES, DEFAULT_LOCALE, resolveLocale } from '@/i18n/index.js';
import api from '@/api/axios.js';
import { useAuthStore } from './auth.js';

export const LOCALE_OPTIONS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Francais' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'cs', label: 'Cestina' },
];

export const useLocaleStore = defineStore('locale', () => {
  const locale = ref(resolveLocale(localStorage.getItem('locale') || i18n.global.locale.value || DEFAULT_LOCALE));

  async function setLocale(nextLocale, options = {}) {
    const { syncBackend = true } = options;
    const safeLocale = resolveLocale(nextLocale);
    locale.value = safeLocale;
    i18n.global.locale.value = safeLocale;
    localStorage.setItem('locale', safeLocale);
    document.documentElement.setAttribute('lang', safeLocale);

    // Speichere die Sprache auf dem Backend wenn der Benutzer angemeldet ist
    try {
      const auth = useAuthStore();
      if (syncBackend && auth.isLoggedIn) {
        await api.post('/auth/set-language', { language: safeLocale });
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Sprache:', err.message);
      // Fehler nicht werfen - die Sprache wird trotzdem lokal gewechselt
    }
  }

  async function initWithUserLanguage() {
    try {
      const auth = useAuthStore();
      if (auth.isLoggedIn && auth.user?.language) {
        // Lade die Sprache des Benutzers wenn angemeldet
        await setLocale(auth.user.language, { syncBackend: false });
      } else {
        // Verwende die lokale oder Standard-Sprache
        await setLocale(locale.value, { syncBackend: false });
      }
    } catch (err) {
      console.error('Fehler beim Initialisieren der Sprache:', err.message);
      // Fallback auf lokale Sprache
      await setLocale(locale.value, { syncBackend: false });
    }
  }

  function init() {
    // Nur die lokale Sprache setzen, nicht auf den Benutzer warten
    setLocale(locale.value, { syncBackend: false });
  }

  const options = computed(() => LOCALE_OPTIONS.filter((entry) => SUPPORTED_LOCALES.includes(entry.code)));

  return { locale, options, setLocale, init, initWithUserLanguage };
});
